import {match} from 'ts-pattern';

import {isBoolean, isString} from 'ramda-adjunct';

import {DEFAULT_COL_WIDTH} from '../constants/defaultColWidth';
import type {
  ColumnResponseBody,
  ColumnsSetting,
  EntityColumnResponseBody,
  GetDataQueryResponse,
  TransformedDefinitionApiResponse,
} from '../types/Api';
import type {EagColumn} from '../types/EagColumn';

type SortEntry = {column: string; order: 'ASC' | 'DESC'};

// eslint-disable-next-line max-params -- Pre-existing violation
export const columnToBasicDefs = (
  dataQuery: GetDataQueryResponse,
  definition: TransformedDefinitionApiResponse,
  column: ColumnResponseBody,
  defaultPresetColumn?: ColumnsSetting,
  defaultPresetOrder?: SortEntry,
  defaultPresetOrderIndex?: number
): Partial<EagColumn> => {
  const isNewlyAddedColumnVisible = !definition.behavior.columnToolPanelVisible;

  // Definition-hidden columns never seed a sort: a stale preset/query order entry would
  // otherwise sort the grid by a column with no visible header or panel entry to clear it.
  const sortEntryIndex = column.hidden
    ? -1
    : (dataQuery.order?.findIndex((ord) => ord.columnKey === column.key) ?? -1);
  const sort =
    sortEntryIndex >= 0 ? (dataQuery.order[sortEntryIndex].order as 'ASC' | 'DESC') : undefined;

  const pinned = match<ColumnsSetting['pinned'] | undefined, EagColumn['pinned']>(
    defaultPresetColumn?.pinned
  )
    .when(isBoolean, (p) => (p === true ? 'LEFT' : null))
    .when(isString, (p) => (p.toUpperCase() === 'RIGHT' ? 'RIGHT' : 'LEFT') as 'LEFT' | 'RIGHT')
    .otherwise(() => null);

  const isVisible = defaultPresetColumn?.visible ?? isNewlyAddedColumnVisible;

  return {
    key: column.key,
    headerName: column.title,
    field: column.key,
    cellClass: 'ellipsis',
    hidden: !isVisible || column.hidden,
    lockedHidden: column.hidden,
    width: defaultPresetColumn?.width ? defaultPresetColumn.width : DEFAULT_COL_WIDTH,
    sortable: column.sortable,
    toolPanelClass: `columnSettings-${column.key}`,
    pinned,
    // ENTITY columns carry a resourceId (EntityColumnResponseBody) that
    // LinkCellRenderer uses as the column-level resolver fallback.
    ...('resourceId' in column
      ? {resourceId: (column as EntityColumnResponseBody).resourceId}
      : {}),
    ...(sort != null ? {sort} : {}),
    ...(defaultPresetOrderIndex != null && !column.hidden // eslint-disable-line no-nested-ternary -- Pre-existing violation
      ? {sortIndex: defaultPresetOrderIndex}
      : sortEntryIndex >= 0
        ? {sortIndex: sortEntryIndex}
        : {}),
  };
};
